import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchAuthStatus,
  fetchMeOptional,
  postLogin,
  postLogout,
} from '../services/authApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function toUser(me) {
  return {
    id: me.userId,
    principalName: me.principalName,
    displayName: me.principalName,
  };
}

export const AuthProvider = ({ children }) => {
  const [authReady, setAuthReady] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchAuthStatus();
        if (cancelled) return;
        setAuthRequired(status.authRequired);
        if (!status.authRequired) {
          setUser(null);
          setAuthReady(true);
          return;
        }
        // JSESSIONID is HttpOnly — do not gate on document.cookie visibility.
        const me = await fetchMeOptional();
        if (cancelled) return;
        setUser(me ? toUser(me) : null);
        setAuthReady(true);
      } catch {
        if (!cancelled) {
          setAuthRequired(false);
          setUser(null);
          setAuthReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username, password) => {
    const me = await postLogin(username, password);
    const next = toUser(me);
    setUser(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    try {
      await postLogout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      authReady,
      authRequired,
      isAuthenticated: !!user,
      user,
      loading: !authReady,
      login,
      logout,
    }),
    [authReady, authRequired, user, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
